import { useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useRiskReport, RiskLevel } from './useRiskReportController';

export type CohortStatusFilter = 'all' | 'active' | 'upcoming';

export const useRiskReportDialogController = (open: boolean) => {
  const { data, isLoading } = useRiskReport(open);
  const [riskFilter, setRiskFilter] = useState<RiskLevel | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [cohortStatusFilter, setCohortStatusFilter] = useState<CohortStatusFilter>('all');

  const filteredCohorts = useMemo(() => {
    if (!data) return [];

    return data.cohorts
      .filter((cohort) => {
        if (cohortStatusFilter === 'active') return !cohort.isUpcoming;
        if (cohortStatusFilter === 'upcoming') return cohort.isUpcoming;
        return true;
      })
      .map((cohort) => {
        let participants = cohort.participants;

        // Filter by active status
        if (!showInactive) {
          participants = participants.filter((p) => p.isActive);
        }

        // Filter by risk level
        if (riskFilter !== 'all') {
          participants = participants.filter((p) => p.riskLevel >= (riskFilter as RiskLevel));
        }

        return { ...cohort, participants };
      })
      .filter((cohort) => cohort.participants.length > 0);
  }, [data, riskFilter, showInactive, cohortStatusFilter]);

  const maxWeeks = useMemo(() => {
    if (!filteredCohorts.length) return 0;
    return Math.max(...filteredCohorts.flatMap((c) => c.participants.map((p) => p.weeks.length)));
  }, [filteredCohorts]);

  const inactiveCount = useMemo(() => {
    if (!data) return 0;
    return data.cohorts.reduce((sum, cohort) => sum + cohort.participants.filter((p) => !p.isActive).length, 0);
  }, [data]);

  const upcomingCount = useMemo(() => {
    if (!data) return 0;
    return data.cohorts.filter((c) => c.isUpcoming).length;
  }, [data]);

  const handleFilterChange = (e: SelectChangeEvent) => {
    const val = e.target.value;
    setRiskFilter(val === 'all' ? 'all' : (Number(val) as RiskLevel));
  };

  const handleCohortStatusFilterChange = (e: SelectChangeEvent) => {
    setCohortStatusFilter(e.target.value as CohortStatusFilter);
  };

  return {
    data,
    isLoading,
    riskFilter,
    filteredCohorts,
    maxWeeks,
    showInactive,
    setShowInactive,
    inactiveCount,
    upcomingCount,
    cohortStatusFilter,
    handleFilterChange,
    handleCohortStatusFilterChange,
  };
};
